import { HamburgerIcon } from 'assets/icons/HamburgerIcon';
import { SelectClientCompanyIcon } from 'assets/icons/SelectClientCompanyIcon';
import { IconButton } from 'components/IconButton/IconButton';
import { SelectInput } from 'components/Inputs/SelectInput/SelectInput';
import { HumBurgerSidePanel } from 'pages/HamBurger/HamBurgerSlidePanel';
import { ChangeEvent, FunctionComponent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useAppDispatch, usePersistReduxStoreAndReloadPage } from 'redux/hooks';
import { getSelectedPermission, getUserPermissions, isBackOfficeUser } from 'redux/User/selectors';
import { updateSelectedPermissionId } from 'redux/User/slice';
import { ROUTES } from 'routing/routes';
import { PLATFORM_REVISED_LEGAL_CORPORATION_ID } from 'services/authentication/msalConfig';
import { useUserInformation } from 'services/authentication/useUserInformation';
import { convertPermissionToSelectInputOption } from 'services/utils/permissions';

import { LEGAl_CORPORATION_BRAND_LOGOS_BY_ID } from '../../../services/api/constants';
import { useDeviceCategory } from '../useDeviceCategory';
import {
  CreateHamBurgerButtonContainer,
  HeaderBrandImage,
  HeaderBrandImageContainer,
  HeaderContainer,
  LogoContainer,
  SelectionContainer,
  UserContainer,
} from './Header.style';
import { UserCard } from './UserCard/UserCard';

const HEADER_BRAND_LOGO_FILENAME = 'HeaderBrandLogo.png';

interface BrandLogo {
  image: string;
  url: string;
}

export const Header: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const persistReduxStoreAndReloadPage = usePersistReduxStoreAndReloadPage();
  const userInformation = useUserInformation();
  const userPermissions = useSelector(getUserPermissions);
  const userSelectedPermission = useSelector(getSelectedPermission);
  const isBackofficeUser = useSelector(isBackOfficeUser);
  const displayCompanySelectInput = userInformation.isAuthenticated && !isBackofficeUser;
  const SELECT_INPUT_ID = 'userPermissionsSelectInput';
  const legalCorporationID = PLATFORM_REVISED_LEGAL_CORPORATION_ID;
  const { isDesktopWithZoom100 } = useDeviceCategory();

  const userPermissionsForSelectInput = useMemo(() => {
    return userPermissions.map(convertPermissionToSelectInputOption);
  }, [userPermissions]);

  const onSelectInputChange = async (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    dispatch(updateSelectedPermissionId(event.target.value));
    persistReduxStoreAndReloadPage();
  };
  const [isHamBurgerSidePanelOpen, setIsHamBurgerSidePanelOpen] = useState(false);

  const closeMenu = () => setIsHamBurgerSidePanelOpen(false);

  return (
    <HeaderContainer>
      <HumBurgerSidePanel isOpen={isHamBurgerSidePanelOpen} onAbort={closeMenu} />
      <CreateHamBurgerButtonContainer>
        <IconButton
          displayStyle="grey"
          size="small"
          onClick={() => setIsHamBurgerSidePanelOpen(true)}
          disabled={isHamBurgerSidePanelOpen}
        >
          <HamburgerIcon widthInPx={20} heightInPx={22} />
        </IconButton>
      </CreateHamBurgerButtonContainer>
      <LogoContainer>
        {legalCorporationID &&
        LEGAl_CORPORATION_BRAND_LOGOS_BY_ID[legalCorporationID] &&
        isDesktopWithZoom100 ? (
          LEGAl_CORPORATION_BRAND_LOGOS_BY_ID[legalCorporationID].map(
            (logo: BrandLogo, _index: number, logos: BrandLogo[]) => (
              <a href={logo.url} key={logo.image} target="_blank" rel="noreferrer">
                <HeaderBrandImageContainer>
                  <HeaderBrandImage src={`/config/${logo.image}`} $isCompact={logos.length > 6} />
                </HeaderBrandImageContainer>
              </a>
            ),
          )
        ) : (
          <Link to={ROUTES.HOME}>
            <HeaderBrandImageContainer>
              <HeaderBrandImage src={`/config/${HEADER_BRAND_LOGO_FILENAME}`} />
            </HeaderBrandImageContainer>
          </Link>
        )}
      </LogoContainer>
      {displayCompanySelectInput && (
        <SelectionContainer>
          <SelectInput
            id={SELECT_INPUT_ID}
            name={SELECT_INPUT_ID}
            value={userSelectedPermission?.id}
            onChange={onSelectInputChange}
            choices={userPermissionsForSelectInput}
            startIcon={<SelectClientCompanyIcon />}
            preserveSpacingForHelperText={false}
          />
        </SelectionContainer>
      )}
      <UserContainer>
        {userInformation.isAuthenticated && <UserCard user={userInformation} />}
      </UserContainer>
    </HeaderContainer>
  );
};
